import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import BackgroundImage from '../BackgroundImage';
import Banner from '../Banner';
import SideBanners from '../SideBanners';
import { getBannerPosition } from '../../config/bannerConfig';
import useDebounce from '../../hooks/useDebounce';

const DesktopAppLayout = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const isIndexPage = location.pathname === '/';
  
  // Debounce search query with 300ms delay
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  // Track if this is the first render to avoid triggering search on mount
  const isFirstRender = useRef(true);
  // Track the last searched value to avoid duplicate searches
  const lastSearchedValue = useRef('');

  // Effect to handle debounced search with auto-reset
  useEffect(() => {
    // Skip the first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Only act if on IndexPage
    if (!isIndexPage) return;
    
    const trimmed = debouncedSearchQuery.trim();
    
    // Avoid duplicate searches for the same value
    if (trimmed === lastSearchedValue.current) return;
    
    if (trimmed.length === 0) {
      // Search was cleared - reset to all posts
      lastSearchedValue.current = '';
      if (window.indexPageOnReset) {
        window.indexPageOnReset();
      }
    } else if (trimmed.length >= 2) {
      // Minimum 2 characters to trigger search (reduces noise)
      lastSearchedValue.current = trimmed;
      if (window.indexPageOnSearch) {
        window.indexPageOnSearch(trimmed);
      }
    }
  }, [debouncedSearchQuery, isIndexPage]);

  const handleSearch = (query) => {
    // Manual submit - bypass debounce for immediate search
    const trimmed = query.trim();
    if (trimmed.length > 0 && isIndexPage && window.indexPageOnSearch) {
      lastSearchedValue.current = trimmed;
      window.indexPageOnSearch(trimmed);
    }
  };

  const handleReset = () => {
    // Clear search query
    setSearchQuery('');
    lastSearchedValue.current = '';
    
    // If on IndexPage, trigger the reset
    if (isIndexPage && window.indexPageOnReset) {
      window.indexPageOnReset();
    }
  };

  return (
    <div className="desktop-layout desktop-layout--app">
      <BackgroundImage 
        alt="Background"
        className="bg-img"
      />
      <Header 
        onSearch={handleSearch} 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery}
        isSearchEnabled={isIndexPage}
        onReset={handleReset}
      />
      {/* Side banners - vertical banners on left and right of main panel */}
      {/* Configure in: src/config/sideBannersConfig.js */}
      <SideBanners />
      {/* Legacy single side banner support */}
      {(getBannerPosition() === 'side-left' || getBannerPosition() === 'side-right') && <Banner />}
      <main className="desktop-layout__main">
        {getBannerPosition() === 'top' && <Banner />}
        {getBannerPosition() === 'content-top' && <Banner />}
        {React.cloneElement(children, { 
          searchQuery, 
          setSearchQuery,
          onSearch: handleSearch
        })}
        {getBannerPosition() === 'content-bottom' && <Banner />}
        {getBannerPosition() === 'bottom' && <Banner />}
      </main>
      <Footer />
    </div>
  );
};

export default DesktopAppLayout;
